import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by moving the gallery to the selected variant's media,
   * replacing the gallery outright when the media it renders changed.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = /** @type {MediaGallery | null} */ (html.querySelector('media-gallery'));
        if (!newMediaGallery) return;

        const featuredMediaId = detail.resource?.featured_media?.id;
        const slideId = featuredMediaId == null ? null : String(featuredMediaId);

        // The gallery renders the product's media in the order set on the product, so unless the
        // media itself changed the new markup is the same as what is already on the page. Swapping it
        // in would reload every image and drop the slideshow back to its first slide, which is why
        // picking a variant never moved the gallery. Keep the live gallery and just jump to the media
        // the selected variant features, the same way product cards do.
        if (this.#mediaIds().join() === this.#mediaIds(newMediaGallery).join()) {
          if (slideId) this.slideshow?.select({ id: slideId }, undefined, { animate: false });
          return;
        }

        this.replaceWith(newMediaGallery);

        if (slideId) newMediaGallery.slideshow?.select({ id: slideId }, undefined, { animate: false });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * The ids of the media a gallery renders, in slide order.
   *
   * @param {Element} [gallery] - The gallery to read, defaults to this one.
   * @returns {string[]} The media ids.
   */
  #mediaIds(gallery = this) {
    return Array.from(
      gallery.querySelectorAll('slideshow-slide [data-media-id]'),
      (media) => /** @type {HTMLElement} */ (media).dataset.mediaId ?? ''
    );
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
